const SAMPLE_INTERVAL_MS = 50;
// Capture/UI debounce bridges brief natural pauses; it is not a turn boundary.
const SPEAKING_HOLD_MS = 800;
const INTERRUPTION_SILENCE_MS = 350;
const INTERRUPTION_FALLBACK_MS = 1500;
const INTERRUPTION_RECOVERY_LIMIT_MS = 5000;
const SPEECH_RMS_THRESHOLD = 0.012;

export type LiveVoicePlayback = {
  interrupt: () => void;
  dispose: () => void;
};

/** Microphone energy is capture/UI activity, never semantic turn evidence. */
export function observeLiveVoiceInputActivity(args: {
  stream: MediaStream;
  onInputActivity: (active: boolean) => void;
}): { dispose: () => void } {
  let context: AudioContext | undefined;
  let source: MediaStreamAudioSourceNode | undefined;
  let analyser: AnalyserNode | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let active = false;
  let lastSignalAt = -Infinity;
  try {
    if (typeof AudioContext !== 'undefined') {
      context = new AudioContext();
      source = context.createMediaStreamSource(args.stream);
      analyser = context.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      void context.resume().catch(() => undefined);
      const samples = new Float32Array(analyser.fftSize);
      timer = setInterval(() => {
        if (!analyser || context?.state !== 'running') return;
        analyser.getFloatTimeDomainData(samples);
        let energy = 0;
        for (const sample of samples) energy += sample * sample;
        const now = Date.now();
        const enabled = args.stream
          .getAudioTracks()
          .some((track) => track.enabled);
        if (
          enabled &&
          Math.sqrt(energy / samples.length) >= SPEECH_RMS_THRESHOLD
        )
          lastSignalAt = now;
        const next = enabled && now - lastSignalAt < SPEAKING_HOLD_MS;
        if (next !== active) {
          active = next;
          args.onInputActivity(next);
        }
      }, SAMPLE_INTERVAL_MS);
    }
  } catch {
    // Missing metering never turns transcript arrival into media activity.
  }
  return {
    dispose() {
      clearInterval(timer);
      source?.disconnect();
      analyser?.disconnect();
      if (context) void context.close().catch(() => undefined);
      if (active) args.onInputActivity(false);
      active = false;
    },
  };
}

/** Observe decoded media, independently of transcripts and backend responses. */
export function observeLiveVoicePlayback(args: {
  stream: MediaStream;
  audioElement: HTMLAudioElement;
  onPlaybackChange?: (speaking: boolean) => void;
}): LiveVoicePlayback {
  const initiallyMuted = args.audioElement.muted;
  let context: AudioContext | undefined;
  let source: MediaStreamAudioSourceNode | undefined;
  let analyser: AnalyserNode | undefined;
  let timer: ReturnType<typeof setInterval> | undefined;
  let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  let suppressed = false;
  let speaking = false;
  let lastSignalAt = -Infinity;
  let quietSince: number | null = null;

  const report = (next: boolean) => {
    if (speaking === next) return;
    speaking = next;
    args.onPlaybackChange?.(next);
  };
  const recover = () => {
    if (disposed || !suppressed) return;
    suppressed = false;
    quietSince = null;
    // The activity hold from before interruption must not restart a recording
    // after measured silence. A fresh decoded signal can mark activity again.
    lastSignalAt = -Infinity;
    args.audioElement.muted = initiallyMuted;
    clearTimeout(recoveryTimer);
  };
  try {
    if (typeof AudioContext !== 'undefined') {
      context = new AudioContext();
      source = context.createMediaStreamSource(args.stream);
      analyser = context.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      void context.resume().catch(() => undefined);
      const samples = new Float32Array(analyser.fftSize);
      timer = setInterval(() => {
        if (disposed || !analyser || context?.state !== 'running') return;
        analyser.getFloatTimeDomainData(samples);
        let energy = 0;
        for (const sample of samples) energy += sample * sample;
        const now = Date.now();
        const signal =
          Math.sqrt(energy / samples.length) >= SPEECH_RMS_THRESHOLD;
        if (signal) lastSignalAt = now;
        if (suppressed) {
          quietSince = signal ? null : (quietSince ?? now);
          if (
            quietSince !== null &&
            now - quietSince >= INTERRUPTION_SILENCE_MS
          )
            recover();
        }
        report(
          !suppressed &&
            !args.audioElement.muted &&
            !args.audioElement.paused &&
            now - lastSignalAt < SPEAKING_HOLD_MS
        );
      }, SAMPLE_INTERVAL_MS);
    }
  } catch {
    // Audio metering can be unavailable even when WebRTC playback works.
  }

  return {
    interrupt() {
      if (disposed) return;
      suppressed = true;
      quietSince = null;
      args.audioElement.muted = true;
      report(false);
      clearTimeout(recoveryTimer);
      // Muted media continues draining. Prefer measured silence. The timeout
      // avoids a stuck mute if metering stops or no quiet interval arrives;
      // this recovery policy is not proof that the assistant stopped speaking.
      recoveryTimer = setTimeout(
        recover,
        analyser && context?.state === 'running'
          ? INTERRUPTION_RECOVERY_LIMIT_MS
          : INTERRUPTION_FALLBACK_MS
      );
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      clearInterval(timer);
      clearTimeout(recoveryTimer);
      source?.disconnect();
      analyser?.disconnect();
      if (context) void context.close().catch(() => undefined);
      args.audioElement.muted = initiallyMuted;
      report(false);
    },
  };
}
